/**
 * scheduleRuns — the runs themselves, and the printable copy of one.
 *
 * Two asks from Anthony (2026-10-10), both about runs rather than the board:
 *   1. "When a teacher schedule is made, I need a printed copy of it somewhere
 *      for my reference" — without re-setting the range, refreshing and
 *      scrolling. buildPrintLessons + the two groupings feed SchedulePrint.
 *   2. The next schedule should start the day after the last one ended
 *      ("I've only scheduled up until October 25, so ... the next start date
 *      should be October 26"). nextScheduleRange.
 *
 * TIME RULE (schedulingModel.js header): events are stored wall-time-as-UTC and
 * are read in UTC, never in a timezone. Run bounds are UTC midnight /
 * end-of-day of the studio date, so their UTC date IS the studio date.
 *
 * Pure; pinned by scheduleRuns.test.js.
 */
import {
	LOCATION_LABELS,
	addDays,
	compareLocations,
	formatEventTime,
	lessonStudentName,
	locationKey,
	startOfWeek,
	timestampToDayKey,
} from './schedulingModel';

/** Matches the length of the runs made so far (Sep 28 → Oct 25 = 28 days). */
export const NEXT_RANGE_DAYS = 28;

/** Same rule as teacherViewData.pickReleasedRun: no status = released. */
export const isReleasedRun = (run) => Boolean(run) && run.status !== 'draft';

/** A run's range as day keys, or nulls when it carries none. */
export const runRangeKeys = (run) => ({
	from: timestampToDayKey(run?.rangeStart),
	to: timestampToDayKey(run?.rangeEnd),
});

const createdMs = (run) => {
	const c = run?.createdAt;
	if (!c) return 0;
	if (typeof c.toMillis === 'function') return c.toMillis();
	const d = typeof c.toDate === 'function' ? c.toDate() : new Date(c);
	return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

/** Newest first by createdAt — the order every run list on the page uses. */
export const sortRunsNewestFirst = (runs) => [...(runs || [])].sort((a, b) => createdMs(b) - createdMs(a));

/**
 * The range the NEXT schedule should cover: the day after the furthest-reaching
 * RELEASED run, for NEXT_RANGE_DAYS. Furthest END, not newest createdAt — a
 * re-release of an older month must not pull the suggestion backwards. Drafts
 * are ignored: a draft is not a schedule anyone has committed to. Null when no
 * run has ever been released.
 */
export const nextScheduleRange = (runs) => {
	let lastEnd = null;
	for (const run of runs || []) {
		if (!isReleasedRun(run)) continue;
		const { to } = runRangeKeys(run);
		if (to && (!lastEnd || to > lastEnd)) lastEnd = to;
	}
	if (!lastEnd) return null;
	const from = addDays(lastEnd, 1);
	return { from, to: addDays(from, NEXT_RANGE_DAYS - 1) };
};

const MONTH_DAY = { timeZone: 'UTC', month: 'short', day: 'numeric' };

const dayKeyLabel = (dayKey, options) =>
	new Date(`${dayKey}T12:00:00.000Z`).toLocaleDateString('en-US', { timeZone: 'UTC', ...options });

/** "Sep 28 – Oct 25, 2026" for a run, or "No range" when it has none. */
export const formatRunRange = (run) => {
	const { from, to } = runRangeKeys(run);
	if (!from || !to) return 'No range';
	const year = to.slice(0, 4);
	return `${dayKeyLabel(from, MONTH_DAY)} – ${dayKeyLabel(to, MONTH_DAY)}, ${year}`;
};

/** What the run picker shows: the range plus where the run stands. */
export const runOptionLabel = (run) => `${formatRunRange(run)} · ${isReleasedRun(run) ? 'released' : 'draft'}`;

/** "Sunday, Sep 28" — a print heading. */
export const formatPrintDay = (dayKey) => dayKeyLabel(dayKey, { weekday: 'long', month: 'short', day: 'numeric' });

export const UNASSIGNED = 'Unassigned';

/**
 * Every lesson in [from, to] as one flat row, sorted by day, time, location.
 * The teacher is resolved PER LESSON from its own assignment (a timeslot rule
 * can hand one slot to a substitute), never from the day's majority.
 */
export const buildPrintLessons = ({ events, assignmentsByEventId, from, to, teacherNameById }) => {
	const rows = [];
	for (const event of events || []) {
		const dayKey = timestampToDayKey(event.start);
		if (!dayKey || (from && dayKey < from) || (to && dayKey > to)) continue;
		const assignment = assignmentsByEventId?.get(event.id) || null;
		const teacherId = assignment?.teacherId || null;
		const location = locationKey(event.location);
		rows.push({
			eventId: event.id,
			dayKey,
			startMs: event.start?.toDate ? event.start.toDate().getTime() : 0,
			time: formatEventTime(event.start),
			studentName: lessonStudentName(event.summary),
			location,
			locationLabel: LOCATION_LABELS[location] || location,
			teacherId,
			teacherName: teacherId ? teacherNameById?.get(teacherId) || 'Unknown teacher' : UNASSIGNED,
		});
	}
	return rows.sort(
		(a, b) => a.dayKey.localeCompare(b.dayKey) || a.startMs - b.startMs || compareLocations(a.location, b.location)
	);
};

/** Lessons grouped by day; within a day, by location, each naming its teacher(s). */
export const groupByDay = (lessons) => {
	const days = new Map();
	for (const lesson of lessons) {
		if (!days.has(lesson.dayKey)) days.set(lesson.dayKey, new Map());
		const locations = days.get(lesson.dayKey);
		if (!locations.has(lesson.location)) locations.set(lesson.location, []);
		locations.get(lesson.location).push(lesson);
	}
	return [...days.entries()].map(([dayKey, locations]) => ({
		dayKey,
		label: formatPrintDay(dayKey),
		locations: [...locations.entries()]
			.sort(([a], [b]) => compareLocations(a, b))
			.map(([location, rows]) => ({
				location,
				label: rows[0].locationLabel,
				// The day's teacher(s) in first-lesson order: one name for a whole
				// day, two for a split day — never a silent pick of the first.
				teachers: [...new Set(rows.map((r) => r.teacherName))],
				lessons: rows,
			})),
	}));
};

/**
 * Lessons grouped by teacher (alphabetical, Unassigned last), then by day —
 * each teacher's own sheet, with lesson and day counts.
 */
export const groupByTeacher = (lessons) => {
	const teachers = new Map();
	for (const lesson of lessons) {
		const key = lesson.teacherId || UNASSIGNED;
		if (!teachers.has(key)) teachers.set(key, { teacherId: lesson.teacherId, teacherName: lesson.teacherName, lessons: [] });
		teachers.get(key).lessons.push(lesson);
	}
	return [...teachers.values()]
		.sort((a, b) => {
			if (!a.teacherId !== !b.teacherId) return a.teacherId ? -1 : 1;
			return a.teacherName.localeCompare(b.teacherName);
		})
		.map((t) => {
			const days = new Map();
			for (const lesson of t.lessons) {
				if (!days.has(lesson.dayKey)) days.set(lesson.dayKey, []);
				days.get(lesson.dayKey).push(lesson);
			}
			return {
				...t,
				lessonCount: t.lessons.length,
				dayCount: days.size,
				days: [...days.entries()].map(([dayKey, rows]) => ({ dayKey, label: formatPrintDay(dayKey), lessons: rows })),
			};
		});
};

/**
 * Days (from groupByDay) gathered into board weeks, for the on-screen reading
 * view: a month of day cards reads as four labelled weeks rather than one long
 * wall. Same week boundary as the board (schedulingModel.startOfWeek).
 */
export const groupDaysByWeek = (days) => {
	const weeks = new Map();
	for (const day of days) {
		const weekStart = startOfWeek(day.dayKey);
		// Labelled by the first day SHOWN, not the Sunday the week starts on: a
		// run beginning Monday Oct 26 read "Week of Oct 25", a day it does not
		// contain.
		if (!weeks.has(weekStart)) {
			weeks.set(weekStart, { weekStart, label: `Week of ${dayKeyLabel(day.dayKey, MONTH_DAY)}`, days: [] });
		}
		weeks.get(weekStart).days.push(day);
	}
	return [...weeks.values()];
};

/** "Tue" and "Oct 6" for a day card's two-line heading. */
export const dayCardHeading = (dayKey) => ({
	weekday: dayKeyLabel(dayKey, { weekday: 'short' }),
	date: dayKeyLabel(dayKey, MONTH_DAY),
});
